import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Eyebrow, MaskLines, Reveal, ease } from "./motion";
import { Lightbox } from "./Lightbox";

const IMGS = ["/img/plan-lobby.jpg", "/img/plan-floor1.jpg", "/img/plan-floor2.jpg"];

export const Plans = () => {
  const { t } = useLang();
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const tab = t.plans.tabs[active];

  return (
    <section id="plans" className="relative bg-[#F2F9F9] py-24 md:py-36" data-testid="plans-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Eyebrow>{t.plans.eyebrow}</Eyebrow>
          <h2 className="mt-6 font-display text-[clamp(2.4rem,4.4vw,4rem)] leading-[1] text-[var(--ink)]">
            <MaskLines key={t.plans.title} lines={[t.plans.title]} />
          </h2>
          <Reveal><p className="mt-7 text-[15px] leading-relaxed text-[var(--ink-soft)]">{t.plans.body}</p></Reveal>
          <div className="mt-10 border-t border-[var(--line-strong)]" role="tablist">
            {t.plans.tabs.map((p, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                data-testid={`plans-tab-${i}`}
                className="group relative flex w-full items-baseline justify-between gap-4 border-b border-[var(--line-strong)] py-5 text-start"
              >
                <span className={`absolute bottom-[-1px] start-0 h-px bg-[var(--brass-deep)] transition-[width] duration-700 ${active === i ? "w-full" : "w-0 group-hover:w-1/3"}`} />
                <span className="flex items-baseline gap-4">
                  <span className="text-[12px] tabular-nums text-[var(--brass-ink)]">0{i + 1}</span>
                  <span className={`font-display text-2xl md:text-3xl transition-colors ${active === i ? "text-[var(--ink)]" : "text-[var(--ink-soft)] group-hover:text-[var(--ink)]"}`}>{p.label}</span>
                </span>
                <span className="text-[12px] uppercase tracking-[0.2em] text-[var(--ink-soft)]">{p.meta}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8">
          <Reveal>
            <button onClick={() => setZoom(true)} className="group relative block w-full overflow-hidden rounded-[28px] bg-white p-3 md:p-6 shadow-[0_30px_60px_-35px_rgba(14,95,115,0.35)]" data-testid="plans-image-btn" aria-label={t.plans.enlarge}>
              <div className="relative aspect-[1320/800] w-full">
                <AnimatePresence initial={false}>
                  <motion.img
                    key={IMGS[active]}
                    src={IMGS[active]}
                    alt={`${tab.label} — ${tab.meta}`}
                    initial={{ opacity: 0, scale: 1.03, filter: "blur(6px)" }}
                    animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.9, ease }}
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                </AnimatePresence>
              </div>
              <span className="absolute top-5 end-5 flex items-center gap-2 rounded-full bg-[var(--brass-deep)] px-3.5 py-2 text-[12px] text-white opacity-90 transition-transform duration-500 group-hover:scale-105">
                <Maximize2 className="h-3.5 w-3.5" />{t.plans.enlarge}
              </span>
            </button>
          </Reveal>
          <div className="mt-8 grid gap-6 md:grid-cols-[1fr_auto] md:items-start">
            <motion.p key={`${active}-${tab.desc}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} className="font-display text-2xl leading-snug text-[var(--ink)] max-w-2xl" data-testid="plans-desc">
              {tab.desc}
            </motion.p>
          </div>
          <p className="mt-6 max-w-2xl text-[12px] leading-relaxed text-[var(--ink-soft)]">{t.plans.note}</p>
        </div>
      </div>
      <Lightbox open={zoom} onClose={() => setZoom(false)} src={IMGS[active]} caption={`${tab.label} · ${tab.meta}`} contain />
    </section>
  );
};
